/**
 * FeaturesPage — Products & Features · Features.
 *
 * Reads /v3/config/features. Shows the canonical 5 services + any
 * additional active feature rows.
 *
 * WRITE surface (Bill 2026-06-13): New / Edit / soft-delete via the typed
 * `client.retail.admin.products.features` sub-client (bearer-only — first
 * positional bypassKey arg is the empty string `''`, no bypass prompt).
 * Structure mirrors Identity/RolesPage.tsx.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Feature } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, TextArea, SelectField, BoolField, NumberField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface EditDraft {
  isNew: boolean;
  code: string;
  displayName: string;
  categoryCode: string;
  subCategory: string;
  sortOrder: number | null;
  coreField: string;
  coreAction: string;
  hasToggle: boolean;
  hasAmount: boolean;
  isFollowUp: boolean;
  followUpDays: number | null;
  requiresDocument: boolean;
  isCrossSell: boolean;
  isActive: boolean;
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    code: '',
    displayName: '',
    categoryCode: '',
    subCategory: '',
    sortOrder: null,
    coreField: '',
    coreAction: '',
    hasToggle: false,
    hasAmount: false,
    isFollowUp: false,
    followUpDays: null,
    requiresDocument: false,
    isCrossSell: false,
    isActive: true,
  };
}

function rowToDraft(f: Feature): EditDraft {
  return {
    isNew: false,
    code: f.code,
    displayName: f.displayName,
    categoryCode: f.categoryCode,
    subCategory: f.subCategory ?? '',
    sortOrder: f.sortOrder ?? null,
    coreField: f.coreField ?? '',
    coreAction: f.coreAction ?? '',
    hasToggle: f.hasToggle,
    hasAmount: f.hasAmount,
    isFollowUp: f.isFollowUp,
    followUpDays: f.followUpDays ?? null,
    requiresDocument: f.requiresDocument,
    isCrossSell: f.isCrossSell,
    isActive: f.isActive,
  };
}

export function FeaturesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Feature> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await client.retail.config.features();
      if (resp.success && Array.isArray(resp.data)) {
        setRows(resp.data);
      } else {
        setError(resp.error ?? 'Failed to load features.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Category options derived from the distinct categoryCodes already present
  // in the catalog (FeatureCategory has no closed enum). The current draft
  // value is always merged in so an existing row's category never disappears.
  const categoryOptions = useMemo(() => {
    const codes = new Set<string>();
    for (const f of rows ?? []) {
      if (f.categoryCode) codes.add(f.categoryCode);
    }
    if (draft?.categoryCode) codes.add(draft.categoryCode);
    return Array.from(codes)
      .sort((a, b) => a.localeCompare(b))
      .map((c) => ({ value: c, label: c }));
  }, [rows, draft]);

  const remove = useCallback(
    async (f: Feature): Promise<void> => {
      if (!window.confirm(`Soft-delete feature "${f.code}"? It can be re-activated later.`)) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.products.features.remove('', f.code);
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<Feature>> = useMemo(() => [
    {
      header: 'Code',
      cell: (f) => <code className="cell-mono--bold">{f.code}</code>,
    },
    { header: 'Display Name', cell: (f) => f.displayName },
    {
      header: 'Category',
      cell: (f) => <Pill tone="muted">{f.categoryCode}</Pill>,
    },
    {
      header: 'Toggle / Amount',
      cell: (f) => (
        <div className="pill-stack">
          {f.hasToggle && <Pill tone="info">toggle</Pill>}
          {f.hasAmount && <Pill tone="info">$ amount</Pill>}
          {!f.hasToggle && !f.hasAmount && <span className="cell-dim">—</span>}
        </div>
      ),
    },
    {
      header: 'Follow-up',
      cell: (f) =>
        f.isFollowUp ? (
          <Pill tone="warn">{f.followUpDays ? `${f.followUpDays}d` : 'yes'}</Pill>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: 'Document',
      cell: (f) => (f.requiresDocument ? <Pill tone="info">required</Pill> : <span className="cell-dim">—</span>),
    },
    {
      header: 'Status',
      cell: (f) => (f.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
    },
    {
      header: '',
      cell: (f) => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(f))}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void remove(f)}>
            Delete
          </Button>
        </div>
      ),
    },
  ], [remove]);

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      setSaving(true);
      setSaveErr(null);
      try {
        const body = {
          code: d.code.trim(),
          displayName: d.displayName.trim(),
          categoryCode: d.categoryCode.trim(),
          subCategory: d.subCategory.trim() || null,
          sortOrder: d.sortOrder,
          coreField: d.coreField.trim() || null,
          coreAction: d.coreAction.trim() || null,
          hasToggle: d.hasToggle,
          hasAmount: d.hasAmount,
          isFollowUp: d.isFollowUp,
          followUpDays: d.followUpDays,
          requiresDocument: d.requiresDocument,
          isCrossSell: d.isCrossSell,
          isActive: d.isActive,
        };
        const resp = await client.retail.admin.products.features.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) return <Banner tone="err">Could not load features: {error}</Banner>;
  if (!rows) return <Spinner label="Loading features…" />;

  return (
    <>
      <Card
        title={`Features — ${rows.length}`}
        actions={
          <Button size="sm" onClick={() => setDraft(blankDraft())}>
            New
          </Button>
        }
      >
        <DataTable<Feature>
          columns={columns}
          rows={rows}
          rowKey={(f) => f.code}
          emptyState={<p className="empty">No features defined.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft ? (draft.isNew ? 'New feature' : `Edit feature — ${draft.code}`) : ''}
        onClose={() => (saving ? undefined : setDraft(null))}
        width={580}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <TextField
              label="Code"
              value={draft.code}
              onChange={(v) => setDraft({ ...draft, code: v })}
              disabled={!draft.isNew}
              monospace
              hint={draft.isNew ? 'Stable identifier — read-only after create.' : 'Read-only after create.'}
              required
            />
            <TextField
              label="Display Name"
              value={draft.displayName}
              onChange={(v) => setDraft({ ...draft, displayName: v })}
              required
            />
            <SelectField
              label="Category"
              value={draft.categoryCode}
              onChange={(v) => setDraft({ ...draft, categoryCode: v })}
              options={categoryOptions}
              placeholder="Select a category…"
            />
            <TextField
              label="Sub-category"
              value={draft.subCategory}
              onChange={(v) => setDraft({ ...draft, subCategory: v })}
            />
            <NumberField
              label="Sort Order"
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v })}
            />
            <TextField
              label="Core Field"
              value={draft.coreField}
              onChange={(v) => setDraft({ ...draft, coreField: v })}
              monospace
              hint="Core Director F-number / field destination."
            />
            <TextField
              label="Core Action"
              value={draft.coreAction}
              onChange={(v) => setDraft({ ...draft, coreAction: v })}
              monospace
            />
            <BoolField
              label="Has toggle"
              value={draft.hasToggle}
              onChange={(v) => setDraft({ ...draft, hasToggle: v })}
            />
            <BoolField
              label="Has amount"
              value={draft.hasAmount}
              onChange={(v) => setDraft({ ...draft, hasAmount: v })}
            />
            <BoolField
              label="Is follow-up"
              value={draft.isFollowUp}
              onChange={(v) => setDraft({ ...draft, isFollowUp: v })}
            />
            <NumberField
              label="Follow-up Days"
              value={draft.followUpDays}
              onChange={(v) => setDraft({ ...draft, followUpDays: v })}
              min={0}
              disabled={!draft.isFollowUp}
              hint="Applies when “Is follow-up” is on."
            />
            <BoolField
              label="Requires document"
              value={draft.requiresDocument}
              onChange={(v) => setDraft({ ...draft, requiresDocument: v })}
            />
            <BoolField
              label="Cross-sell"
              value={draft.isCrossSell}
              onChange={(v) => setDraft({ ...draft, isCrossSell: v })}
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
